import { findPrebuiltProviderByProviderId } from "@arcadeai/design-system/metadata/utils";
import type { AuthInfo } from "../../types";
import {
  getAuthToolErrorMessage,
  isAuthToolError,
} from "../tool-invocation-error";

export type ToolAuthStatus = "pending" | "completed" | "failed" | "stopped";

type ToolAuthCard = {
  displayName: string;
  status: ToolAuthStatus;
  message?: string;
  url?: string | null;
  authInfo?: AuthInfo | null;
};

const DISPLAY_LABEL_DELIMITER_RE = /[-_]+/;
const TOOLKIT_NAME_DELIMITER_RE = /[_.]/;

export function getToolkitName(toolName: string): string {
  const idx = toolName.search(TOOLKIT_NAME_DELIMITER_RE);
  return idx > 0 ? toolName.slice(0, idx) : toolName;
}

function normalizeToolName(value: string): string {
  return value.split("@")[0].replaceAll(".", "_");
}

function toDisplayLabel(value: string): string {
  return value
    .split(DISPLAY_LABEL_DELIMITER_RE)
    .filter(Boolean)
    .map((part) =>
      part.length <= 3
        ? part.toUpperCase()
        : part[0].toUpperCase() + part.slice(1)
    )
    .join(" ");
}

export function isSafeExternalUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.trim().length === 0) {
    return false;
  }

  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function openExternalUrl(url: string) {
  if (typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function getAutoOpenAttemptKey(
  authInfo?: AuthInfo | null
): string | null {
  return authInfo?.authorizationId ?? authInfo?.url ?? null;
}

function findAuthInfo(
  name: string,
  authInfoMap?: Map<string, AuthInfo>
): AuthInfo | null {
  if (!authInfoMap) {
    return null;
  }

  const normalizedName = normalizeToolName(name);

  for (const info of authInfoMap.values()) {
    if (
      info.toolNames.some(
        (toolName) => normalizeToolName(toolName) === normalizedName
      )
    ) {
      return info;
    }
  }

  return null;
}

function getAuthDisplayName(name: string, authInfo?: AuthInfo | null): string {
  const providerId = authInfo?.providerId ?? authInfo?.providerKey;
  const provider = providerId
    ? findPrebuiltProviderByProviderId(providerId)
    : undefined;

  return (
    provider?.name ??
    (authInfo?.providerKey
      ? toDisplayLabel(authInfo.providerKey)
      : undefined) ??
    getToolkitName(name)
  );
}

export function resolveToolAuthCard(args: {
  name: string;
  authInfoMap?: Map<string, AuthInfo>;
  result: unknown;
}): ToolAuthCard | null {
  const { name, authInfoMap, result } = args;
  const authInfo = findAuthInfo(name, authInfoMap);

  if (authInfo && (authInfo.status === "pending" || result === undefined)) {
    return {
      authInfo,
      displayName: getAuthDisplayName(name, authInfo),
      status: authInfo.status ?? "pending",
      url: authInfo.url,
    };
  }

  if (isAuthToolError(result)) {
    return {
      authInfo,
      displayName: getAuthDisplayName(name, authInfo),
      status: "failed",
      message: getAuthToolErrorMessage(result),
      url: result.url ?? null,
    };
  }

  return null;
}
